/**
 * HearSpace Acoustics Engine Configuration
 *
 * This file centralizes configuration for HearSpace API integration.
 * Set environment variables to control behavior in different environments.
 */

/**
 * Enable or disable HearSpace integration
 * Can be controlled via environment variable: REACT_APP_ENABLE_HEARSPACE
 */
export const ENABLE_HEARSPACE = process.env.REACT_APP_ENABLE_HEARSPACE === 'true';

/**
 * HearSpace API configuration
 */
export const HEARSPACE_CONFIG = {
  /**
   * Enable HearSpace for specific tools
   */
  enabledTools: ['conferenceRoomDesigner'] as const,

  /**
   * Default receiver spacing in meters
   * Smaller values = more accurate but slower
   *
   * GRID RESOLUTION OPTIONS:
   * - 0.1m: EASE-like resolution (highest quality, may exceed 5000 receiver limit for large rooms) (CURRENT DEFAULT)
   * - 0.15m: High quality compromise
   * - 0.2m: Good balance of quality and performance
   * - 0.3m: Acceptable quality, faster performance
   * - 0.5m: Coarse grid, fastest performance
   *
   * NOTE: API automatically adjusts spacing if receiver count is outside 100-5000 range
   */
  defaultReceiverSpacing: 0.1,

  /**
   * Alternative spacing profiles for different use cases
   */
  spacingProfiles: {
    easeMatch: 0.1,      // Match EASE fine grid
    highQuality: 0.15,   // Current default
    balanced: 0.2,       // Good quality/speed balance
    performance: 0.3,    // Faster calculations
    draft: 0.5,          // Quick preview
  },

  /**
   * Default receiver height in meters
   * EASE typically uses 1.2m for seated position
   * Standard conference room seated listener height
   */
  defaultReceiverHeight: 1.2,

  /**
   * API call debounce time in milliseconds
   * Prevents excessive API calls during rapid state changes
   * (e.g., when user is adding/moving/deleting multiple speakers)
   */
  debounceTime: 1000,

  /**
   * API request timeout in milliseconds
   */
  requestTimeout: 30000, // 30 seconds

  /**
   * Enable direct sound only mode
   * true = faster calculation, no room acoustics (current mode)
   * false = full acoustic modeling with reflections (EASE-like)
   *
   * NOTE: To match EASE visualizations exactly, set to false and provide Materials array
   */
  directSoundOnly: true,

  /**
   * Default background noise level in dBA
   * Used when directSoundOnly is false
   * EASE typical: 35 dBA for office environment
   */
  defaultNoiseLevel: 35,

  /**
   * Per-speaker TotalGain (dB) applied to EXPANSION speakers only in the HearSpace request.
   *
   * Expansion speakers were being sent at full gain, so they inflated the room-wide mean
   * SPL (DirectSplAwtd). Because the Direct SPL heatmap legend auto-centers on that mean
   * (mean ± 10 dB), distant expansion speakers rescaled the bar's apparent coverage — adding
   * a speaker in one corner made another area look under-covered. Attenuating expansion
   * speakers by this gain reduces their weight in the mean so the bar's coverage stays stable.
   *
   * Value recommended by the acoustics/spec owner. The bar speaker is NOT attenuated.
   */
  expansionSpeakerTotalGain: -16,

  /**
   * Enable logging for HearSpace operations
   */
  enableLogging: process.env.NODE_ENV === 'development',

  /**
   * Enable angle debugging
   * Logs detailed angle conversions for wall speakers
   */
  enableAngleDebugging: process.env.NODE_ENV === 'development',

  /**
   * Enable grid validation warnings
   * Warns when receiver grid is adjusted to meet API limits
   */
  enableGridValidation: true,
};

/**
 * Material mapping from internal acoustics settings to HearSpace materials
 * This maps the 'acoustics' property (good/fair/poor) to room materials
 */
export const ACOUSTICS_TO_MATERIALS = {
  good: {
    Floor: 'Carpet_Heavy',
    Ceiling: 'Acoustic_Tile',
    Wall1: 'Gypsum_Board',
    Wall2: 'Gypsum_Board',
    Wall3: 'Gypsum_Board',
    Wall4: 'Gypsum_Board',
  },
  fair: {
    Floor: 'Carpet_Light',
    Ceiling: 'Gypsum_Board',
    Wall1: 'Gypsum_Board',
    Wall2: 'Gypsum_Board',
    Wall3: 'Gypsum_Board',
    Wall4: 'Gypsum_Board',
  },
  poor: {
    Floor: 'Concrete_Painted',
    Ceiling: 'Concrete_Painted',
    Wall1: 'Concrete_Painted',
    Wall2: 'Concrete_Painted',
    Wall3: 'Concrete_Painted',
    Wall4: 'Concrete_Painted',
  },
} as const;

/**
 * Check if HearSpace should be used for a given tool
 */
export const isHearSpaceEnabled = (tool: string): boolean => {
  return (
    ENABLE_HEARSPACE &&
    HEARSPACE_CONFIG.enabledTools.includes(tool as any)
  );
};

/**
 * Log HearSpace messages (only in development)
 */
export const logHearSpace = (message: string, ...args: any[]): void => {
  // Logging disabled
};

export default HEARSPACE_CONFIG;
